import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { labelMaxLength, labelMinLength } from '../../app.constants';

import { ConfigService } from '../../config.service';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { isLabEdition } from '../../shared/container.utils';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { generateElementId } from '../../shared/string.utils';

interface DialogData {
  projectId: string;
}

export interface DialogResult {
  _id: string
}

@Component({
  host: {
    'class': 'mat-dialog-component',
    'test-id': 'create-subject-dialog',
  },
  selector: 'create-subject-dialog',
  styleUrls: ['./create-subject-dialog.component.scss'],
  templateUrl: './create-subject-dialog.component.html',
})
export class CreateSubjectDialog {
  elementId = generateElementId();
  form: FormGroup;
  labRestriction = false;
  groups = [];

  private subscriptions = {
    projectsService: Subscription.EMPTY,
  }

  constructor(
    private configService: ConfigService,
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
    private dialog: MatDialogRef<CreateSubjectDialog, DialogResult>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private projectsService: ProjectsService,
  ) {
    this.form = new FormGroup({
      projectSelect: new FormControl(this.data.projectId || '', [Validators.required]),
      subjectLabel: new FormControl('', [Validators.required, Validators.maxLength(labelMaxLength), Validators.minLength(labelMinLength)]),
    });
    this.subscriptions.projectsService = this.projectsService.getGroupedProjects([PermissionAction.ContainersCreateHierarchy])
      .subscribe(groupedProjects => {
        this.groups = groupedProjects.map(group => ({
          ...group,
          projects: group.projects.filter(project => {
            if (this.configService.isFeatureEnabled('multiproject') && !isLabEdition(project)) {
              if (this.data.projectId === project._id) {
                this.labRestriction = true;
              }
              return false;
            }
            return true;
          }),
        }));
      });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  submit(): void {
    this.coreApi.subjects.post({
      project: this.form.controls.projectSelect.value,
      code: this.form.controls.subjectLabel.value,
      label: this.form.controls.subjectLabel.value,
    }).subscribe({
      next: (resp) => {
        this.notifications.success('session.addSubject.success');
        this.dialog.close(resp);
      },
      error: (error) => {
        if (error.status === 409) {
          this.notifications.error('session.addSubject.duplicate');
        } else {
          this.logger.error(error);
          this.notifications.error('session.addSubject.error');
        }
      },
    });
  }

  getLabelId(tag: string): string {
    return `${this.elementId}-${tag}`;
  }

  cancel(): void {
    this.dialog.close();
  }
}
